import googleAPI from '../apis/googleAPI';
import {
  DISTANCE_OPTIONS,
  GROUP_EVENT_OPTIONS_SORTED,
  GROUP_OPTIONS,
  VIRTUAL_GROUP_OPTIONS,
} from '../constants/EventFilters';
import {generateFilteredTime} from './Helpers';
import {
  EVENT_TAB_TYPES,
  GROUP_EVENT_TAB_TYPES,
  MY_EVENT_TYPES,
  PAST_EVENT_TYPES,
} from '../constants/EventTabs';
import moment from 'moment';
import {DEFAULT_VIRTUAL_OPTIONS} from '../constants/DefaultFilters';
import { userLocation } from '../models/UserLocation';
import { projectConfig } from '../project-info/projectConfig';
import { tenantProjectInfo } from '../models/TenantProjectInfo';
import { pickProjectConfigValue } from './ProjectConfigPicker';

const DEFAULT_LOCALE = 'Everywhere';
const NO_SAVED_LOCATION_ERROR =
  'Unable to find a location for this account. Please update your address in "Personal Information".';
const NO_CHAPTER_ERROR =
  'Unable to find a chapter for this account. Please update your address in "Preferred Chapter".';
const GOOGLE_API_ERROR =
  'Unable to find the coordinates of that location. Please try again with a different location.';

// this returns a promise with a reject on cases there was an error loading something in and resolves otherwise
// the prime reason this is a promise is due to the usage of googleAPI and keeping behavior consistent
const eventURLFormatter = (state, user) => {
  let {
    locale,
    eventDistance,
    eventCategory,
    eventDate,
    search,
    page,
    sortBy,
    lat,
    long,
    activeTab,
    activeMyEventsType,
    activePastEventType,
    virtualSubtype,
    groupId,
    eventGroupOption,
    eventFilterNavTab,
    workspaceOnly,
    virtualTime,
    challengeId,
  } = state;

  if (
    !Object.values(EVENT_TAB_TYPES).includes(activeTab) &&
    !Object.values(GROUP_EVENT_TAB_TYPES)
  )
    throw new Error(
      `Expecting type "local", "virtual", "my", or "past for activeTab. Received ${activeTab}`,
    );

  // if no locale or locale is set to "everywhere", try and get a usable locale for the google api lookup
  // no longer throws an error if location isn't on the user.
  // at the moment, this function prefers lat/long: if lat/long is present, it will use that over anything else.

  if (!locale || locale === DEFAULT_LOCALE) {
    let {zip, city, state} = userLocation.getUserLocation();

    if (zip && city && state) {
      locale = `${zip.slice(0, 5)} ${city}, ${state}`;
    } else {
      locale = DEFAULT_LOCALE;
    }
  }
  
  let urlParams = [];
  // common parameters to all types
  urlParams.push(`offset=${page * 10}&limit=10`);
  const localSearchingGroups =
    activeTab === EVENT_TAB_TYPES.local && (eventFilterNavTab === 'group' || (pickProjectConfigValue('hasGroups') && !projectConfig.allowsLocalEventsOutsideGroups));
  if (search) urlParams.push(`search=${encodeURIComponent(search)}`);
  if (groupId) urlParams.push(`chapter_id=${groupId}`); //backend expects old filter chapter_id
  if (challengeId) urlParams.push(`challenge_id=${challengeId}`);
  if (eventCategory && eventCategory !== 'all-activities' && GROUP_EVENT_OPTIONS_SORTED[eventCategory])
    urlParams.push(`category=${GROUP_EVENT_OPTIONS_SORTED[eventCategory].slug}`);
  // event categories from the server
  else if (eventCategory && eventCategory !== 'all-activities' && tenantProjectInfo.getValue('eventCategories')[eventCategory])
    urlParams.push(`category=${tenantProjectInfo.getValue('eventCategories')[eventCategory].slug}`);
  if (Object.keys(GROUP_OPTIONS).length > 0 && eventGroupOption) {
    if (localSearchingGroups)
      urlParams.push(`group_filter=${GROUP_OPTIONS[eventGroupOption].slug}`);
    else if (activeTab === 'virtual') {
      // if the default is not properly set to national on the event page, search national here to avoid crash
      // this is for alpha testers who had old filters saved to their device
      if (!(eventGroupOption in VIRTUAL_GROUP_OPTIONS))
        eventGroupOption = 'national';
      urlParams.push(
        `group_filter=${VIRTUAL_GROUP_OPTIONS[eventGroupOption].slug}`,
      );
    }
  }
  if (eventFilterNavTab && eventFilterNavTab === 'member' && !groupId)
    urlParams.push('chapter_id=0'); // sending chapter_id=0 retrieves all member events
  // search on a specific group event page
  if (groupId !== null && groupId !== undefined)
    urlParams.push('chapter_hosted=true');
  else {
    // only the local event filters has the option to only be groups or only be members
    if (localSearchingGroups) urlParams.push('chapter_hosted=true');
    else if (activeTab === 'local' && eventFilterNavTab === 'member')
      urlParams.push('chapter_hosted=false');
  }
  if (activeTab === 'virtual' && virtualTime !== DEFAULT_VIRTUAL_OPTIONS.virtualTime) {
    urlParams.push(`is_all_day=${virtualTime === 'all-day' ? 'True' : 'False'}`);
  }

  if (projectConfig.hasWorkspaces && workspaceOnly == 'True') {
    urlParams.push(`workspace_only=${workspaceOnly}`);
  }

  // type-specific parameters
  if (activeTab === EVENT_TAB_TYPES.my) {
    if (!Object.values(MY_EVENT_TYPES).includes(activeMyEventsType))
      throw new Error(
        'Expecting type "hosting", "upcoming", or "past" for my event tab',
      );

    const dateNowString = moment().toISOString();

    urlParams.push(`sort=date`);
    if (activeMyEventsType === MY_EVENT_TYPES.hosting) {
      urlParams.push(`host_id=${user.id}`);
      urlParams.push(`sort_order=desc`);
    } else if (activeMyEventsType === MY_EVENT_TYPES.upcoming) {
      urlParams.push(`after=${dateNowString}`);
      urlParams.push(`sort_order=asc`);
      urlParams.push(`attendee=${user.id}`);
    } else if (activeMyEventsType === MY_EVENT_TYPES.past) {
      urlParams.push(`before=${dateNowString}`);
      urlParams.push(`sort_order=desc`);
      urlParams.push(`attendee=${user.id}`);
    }
    urlParams = urlParams.join('&');
    return new Promise((resolve) => {
      resolve({urlParams, state});
    });
  } else if (activeTab === GROUP_EVENT_TAB_TYPES.past) {
    const dateNowString = moment().toISOString();
    urlParams.push(`sort=date`);
    urlParams.push(`before=${dateNowString}`);
    urlParams.push(`sort_order=desc`);
    urlParams.push(`sort=date`);
    if (activePastEventType === PAST_EVENT_TYPES.local) {
      urlParams.push('is_virtual=False');
    } else if (activePastEventType === PAST_EVENT_TYPES.virtual) {
      urlParams.push('is_virtual=True');
    }
    urlParams = urlParams.join('&');
    return new Promise((resolve) => {
      resolve({urlParams, state});
    });
  } else {
    // Determine if this used/needed on the web
    state.distanceSliderValue = distanceSliderHandler(eventDistance);

    if (activeTab === EVENT_TAB_TYPES.local) urlParams.push('is_virtual=False');

    const dateFilter = generateFilteredTime(eventDate);

    if (dateFilter) urlParams.push(dateFilter);

    urlParams.push(
      `sort=${sortBy}&sort_order=${
        sortBy === 'date' && eventDate.includes('past') ? 'desc' : 'asc'
      }`,
    );

    if (activeTab === EVENT_TAB_TYPES.virtual) {
      urlParams.push('is_virtual=True');
      if (virtualSubtype && virtualSubtype !== 'all')
        urlParams.push(`virtual_event_category=${virtualSubtype}`);

      // virtual events do not require a location/distance
      return new Promise((resolve) => {
        urlParams = urlParams.join('&');
        resolve({urlParams, state});
      });
    } else if (DISTANCE_OPTIONS[eventDistance].slug && lat && long) {
      if (!groupId) {
        urlParams.push(
          `distance=${DISTANCE_OPTIONS[eventDistance].slug}&lat=${lat}&long=${long}`,
        );
      }
      return new Promise((resolve) => {
        urlParams = urlParams.join('&');
        resolve({urlParams, state});
      });
    } else if (DISTANCE_OPTIONS[eventDistance].slug && locale && (!lat || !long)) {
      if (locale === DEFAULT_LOCALE) {
        // show ALL local events (no need for google api call)
        return new Promise((resolve) => {
          urlParams = urlParams.join('&');
          resolve({urlParams, state});
        });
      } else {
        return new Promise((resolve, reject) => {
          return googleAPI
            .geocode(locale)
            .then((result) => {
              if (!groupId) {
                const lat = result.results[0].geometry.location.lat;
                const long = result.results[0].geometry.location.lng;
                // return lat,long in state to avoid unncessary google api calls
                state.lat = lat;
                state.long = long;
                urlParams.push(
                  `distance=${DISTANCE_OPTIONS[eventDistance].slug}&lat=${lat}&long=${long}`,
                );
              }
              urlParams = urlParams.join('&');
              resolve({urlParams, state});
            })
            .catch((err) => {
              reject(GOOGLE_API_ERROR);
            });
        });
      }
    }
  }
};

// determines the visual aspects of the slider for local events
function distanceSliderHandler(eventDistance) {
  if (eventDistance === '25-miles') {
    return 25;
  } else if (eventDistance === '50-miles') {
    return 100;
  } else if (eventDistance === '100-miles') {
    return 175;
  } else {
    return 250;
  }
}

export {eventURLFormatter};
